import dayjs from 'dayjs';
import z from 'zod';

export const post = {
  body: z
    .discriminatedUnion('part_type', [
      z.object({
        part_type: z.literal('system'),
        part: z.coerce.number().min(1, { error: 'Part is required' }),
        expected_qty: z.coerce
          .number()
          .min(1, { error: 'Quantity is required' }),
        expected_date: z
          .string()
          .nonempty({ error: 'Expected date is required' })
          .refine((val) => dayjs(val).isValid(), {
            error: 'Select a valid date.',
          }),
        reference: z.string().optional(),
      }),
      z.object({
        part_type: z.literal('manual'),
        manual_part_number: z
          .string()
          .nonempty({ error: 'Manual part is required' }),
        expected_qty: z.coerce
          .number()
          .min(1, { error: 'Quantity is required' }),
        expected_date: z
          .string()
          .nonempty({ error: 'Expected date is required' })
          .refine((val) => dayjs(val).isValid(), {
            error: 'Select a valid date.',
          }),
        reference: z.string().optional(),
      }),
    ])
    .array(),
  response: z.object({
    id: z.coerce.number(),
    part: z.coerce.number(),
    expected_qty: z.coerce.number(),
    expected_date: z.string().refine((val) => dayjs(val).isValid()),
    reference: z.string().optional(),
  }),
  params: z.object({
    project_id: z.coerce.number(),
  }),
  query: z.never(),
};
const postSchema = z.object(post);
export type Post = z.infer<typeof postSchema>;

const get = {
  body: z.never(),
  response: z.array(
    z.object({
      id: z.coerce.number(),
      part: z.coerce.number().nullable(),
      part_number: z.string(),
      manual_part_number: z.string(),
      expected_qty: z.coerce.number(),
      expected_date: z.string().refine((val) => dayjs(val).isValid()),
      reference: z.string().optional(),
      created_by: z.object({
        id: z.coerce.number(),
        display_name: z.string(),
      }),
      created_at: z.string().refine((val) => dayjs(val).isValid()),
      is_late: z.boolean(),
      received_qty: z.coerce.number(),
      remaining_qty: z.coerce.number(),
      due: z.string(),
    }),
  ),
  params: z.object({
    project_id: z.coerce.number(),
  }),
  query: z
    .object({
      late: z.boolean().optional(),
      due_today: z.boolean().optional(),
    })
    .optional(),
};
const getSchema = z.object(get);
export type Get = z.infer<typeof getSchema>;

export const patch = {
  body: z
    .object({
      expected_qty: z.coerce.number(),
      expected_date: z.string().refine((val) => dayjs(val).isValid(), {
        error: 'Select a valid date.',
      }),
      reference: z.string(),
    })
    .partial(),
  response: z.object({
    part: z.number(),
    expected_qty: z.number(),
    expected_date: z.string(),
    reference: z.string(),
  }),
  params: z.object({
    id: z.coerce.number().min(1, { error: 'Expected receipt id is required' }),
  }),
  query: z.never(),
};
const patchSchema = z.object(patch);
export type Patch = z.infer<typeof patchSchema>;

const remove = {
  body: z.never(),
  response: z.never(),
  params: z.object({
    id: z.coerce.number().min(1, { error: 'Expected receipt id is required' }),
  }),
  query: z.never(),
};
const deleteSchema = z.object(remove);
export type Delete = z.infer<typeof deleteSchema>;
